import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'

import { schemas, ZInfer } from '@showrunner/scrapi'

import { showAlert } from '#components/Modals'
import { useMst } from '#state'

type BillingErrorCode = ZInfer<typeof schemas.BillingErrorCode>

const billingErrorSchema = z.object({
  status: z.literal(420),
  body: schemas.BillingErrorResponse[420],
})

const redeemErrorMessage = (
  error: { code: BillingErrorCode } | undefined,
): string => {
  switch (error?.code) {
    case 'INVALID_PROMOTION_CODE':
      return 'This code is not valid or has already been used.'
    case 'NOT_NEW_SUBSCRIPTION':
      return 'This code is only for new accounts.'
    case 'STRIPE_UNAVAILABLE':
      return 'Our payment provider is unreachable right now.'
    case 'ACCOUNT_CONFLICT':
    case 'BILLING_DISABLED':
    case 'INVALID_CHECKOUT_SESSION':
    case 'NO_SUBSCRIPTION':
    case 'PAYMENT_FAILED':
    case 'PLAN_SEAT_MAX_REACHED':
    case 'SEAT_CHANGE_NOT_ALLOWED':
    case 'SEAT_REDUCTION_BELOW_USAGE':
    case undefined:
      return 'We could not apply your code.'
  }
}

export const useRedeemPromotionCode = () => {
  const { scrapi } = useMst()

  return useMutation({
    async mutationFn(code: string) {
      const result = await scrapi.billing.redeemPromotionCode({
        body: { code },
      })
      if (result.status !== 200) throw result
    },
  })
}

export const showRedeemErrorAlert = (err: unknown) => {
  const knownError = billingErrorSchema.safeParse(err)
  return showAlert({
    title: 'Your code was not applied',
    children: redeemErrorMessage(
      knownError.success ? knownError.data.body : undefined,
    ),
    buttonLabel: 'Continue',
  })
}
